import type { LaunchListItem, LaunchStatus, NextAction } from './types';
import { billingPlanLabel, launchStatusLabel, nextActionLabel } from './status';

interface LaunchListProps {
  items: LaunchListItem[];
  total: number;
  limit: number;
  offset: number;
  selectedId: string | null;
  loading: boolean;
  onSelect: (launchId: string) => void;
  onPage: (offset: number) => void;
}

function statusTint(status: LaunchStatus): string {
  switch (status) {
    case 'paid_active':
      return 'success';
    case 'payment_recorded':
    case 'paid_paused':
      return 'warning';
    case 'failed':
    case 'cancelled':
      return status === 'failed' ? 'danger' : '';
    case 'checkout_pending':
      return 'info';
    default:
      return '';
  }
}

function nextActionTint(action: NextAction): string {
  switch (action) {
    case 'paid_active':
      return 'success';
    case 'operator_recovery':
      return 'danger';
    case 'wait_for_checkout':
    case 'wait_for_subscription_webhook':
    case 'approve_launch':
      return 'warning';
    case 'send_demo':
    case 'open_checkout':
      return 'info';
    default:
      return '';
  }
}

export function LaunchList({ items, total, limit, offset, selectedId, loading, onSelect, onPage }: LaunchListProps) {
  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <p className="portal-eyebrow">Existing Clinic Demos</p>
          <h3>Demo clinics</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="empty-state" data-testid="launch-list-empty">
          <h3>No demo clinics yet</h3>
          <p>Use Clinic Demo Creation to create the first demo clinic.</p>
        </div>
      ) : (
        <>
          <ul className="list" data-testid="launch-list">
            {items.map((item) => (
              <li key={item.launch_id}>
                <button
                  type="button"
                  className={`list-item ${selectedId === item.launch_id ? 'selected' : ''}`}
                  onClick={() => onSelect(item.launch_id)}
                  aria-pressed={selectedId === item.launch_id}
                  data-testid={`launch-item-${item.launch_id}`}
                >
                  <div className="list-item-title" title={item.name}>{item.name}</div>
                  <div className="list-item-meta" title={item.slug}>
                    {item.slug} · {billingPlanLabel(item.billing_interval)}
                    {item.approved_at ? ` · approved ${new Date(item.approved_at).toLocaleDateString()}` : ''}
                  </div>
                  <div className="status-tags">
                    <span
                      className={`status-tag${statusTint(item.status) ? ` status-tag--${statusTint(item.status)}` : ''}`}
                      data-testid={`status-${item.launch_id}`}
                    >
                      {launchStatusLabel(item.status)}
                    </span>
                    <span
                      className={`status-tag${nextActionTint(item.next_action) ? ` status-tag--${nextActionTint(item.next_action)}` : ''}`}
                      data-testid={`next-action-${item.launch_id}`}
                    >
                      {nextActionLabel(item.next_action)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <div className="pagination" data-testid="launch-pagination">
            <button className="btn btn--secondary" onClick={() => onPage(offset - limit)} disabled={!hasPrev || loading}>
              Previous
            </button>
            <span>
              {offset + 1}–{Math.min(offset + items.length, total)} of {total}
            </span>
            <button className="btn btn--secondary" onClick={() => onPage(offset + limit)} disabled={!hasNext || loading}>
              Next
            </button>
          </div>
        </>
      )}
      {loading && <div className="loading" data-testid="launch-list-loading">Loading…</div>}
    </div>
  );
}
